/**
 * AdminTransactionListPage
 * UC-030 — Admin view of every platform transaction across all users.
 * Modelled on UserTransactionHistoryPage (UC-050) but admin-scoped and
 * with a user-email filter + visible user column on each row.
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useApi } from '../hooks/useApi';
import type { TransactionListResponse, UserListResponse } from '../types';
import { Input, Button } from '../components/ui';
import { Select } from '../components/ui/Select';
import { DatePicker } from '../components/ui/DatePicker';
import { fmtMoney } from '../utils/format';

interface PaginatedResponse<T> {
  items: T[];
  total: number;
  skip: number;
  limit: number;
  has_more: boolean;
}

interface TransactionFilters {
  status?: string;
  type?: string;
  from_date?: string;
  to_date?: string;
  user?: string;
}

export function AdminTransactionListPage() {
  const { t } = useTranslation();

  // Filter state
  const [filters, setFilters] = useState<TransactionFilters>({});
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [userEmail, setUserEmail] = useState<string>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');

  const [showError, setShowError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Status options — mirrors Transaction.status DB enum
  const statusOptions = [
    { value: '', label: t('adminTransactionList.filters.allStatuses', 'All statuses') },
    { value: 'Succeeded', label: t('adminTransactionList.filters.succeeded', 'Succeeded') },
    { value: 'Pending', label: t('adminTransactionList.filters.pending', 'Pending') },
    { value: 'Failed', label: t('adminTransactionList.filters.failed', 'Failed') },
  ];

  // Type options — mirrors Transaction.type DB enum
  const typeOptions = [
    { value: '', label: t('adminTransactionList.filters.allTypes', 'All types') },
    { value: 'CreditPurchase', label: t('adminTransactionList.filters.creditPurchase', 'Credit purchase') },
    { value: 'SubscriptionPayment', label: t('adminTransactionList.filters.subscription', 'Subscription') },
    { value: 'Refund', label: t('adminTransactionList.filters.refund', 'Refund') },
    { value: 'CreditAddition', label: t('adminTransactionList.filters.creditAddition', 'Credit addition') },
  ];

  // Transactions request
  const {
    data: txData,
    loading: txLoading,
    refetch: refetchTx,
  } = useApi<PaginatedResponse<TransactionListResponse>>(
    '/transactions',
    {
      params: { limit: 100, ...filters },
      onError: (err) => {
        setShowError(true);
        setErrorMessage(
          err.message ||
            t('adminTransactionList.errors.loadFailed', 'Failed to load transactions'),
        );
        toast.error(t('adminTransactionList.errors.loadFailed', 'Failed to load transactions'));
      },
      onSuccess: () => setShowError(false),
    },
  );

  // Users request — for mapping userId -> display name on each row. Limit
  // at backend max (100) so even later-id users resolve instead of showing
  // "Unknown". Same band-aid we applied to the other admin pages.
  const { data: usersData } = useApi<PaginatedResponse<UserListResponse>>(
    '/users',
    { params: { limit: 100 } },
  );
  const users = usersData?.items || [];
  const userById = new Map(users.map((u) => [u.userId, u]));

  // Refetch when filters change (useApi only auto-fetches on mount).
  useEffect(() => {
    refetchTx();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const updateFilters = (next: Partial<TransactionFilters>) => {
    const merged = { ...filters, ...next };
    (Object.keys(merged) as (keyof TransactionFilters)[]).forEach((k) => {
      if (merged[k] === undefined || merged[k] === '') delete merged[k];
    });
    setFilters(merged);
  };

  const handleStatus = (v: string) => { setStatusFilter(v); updateFilters({ status: v || undefined }); };
  const handleType = (v: string) => { setTypeFilter(v); updateFilters({ type: v || undefined }); };
  const handleFrom = (v: string) => { setFromDate(v); updateFilters({ from_date: v || undefined }); };
  const handleTo = (v: string) => { setToDate(v); updateFilters({ to_date: v || undefined }); };

  // User/email filter is applied on submit (Enter or the search button) so
  // we don't fire a request on every keystroke.
  const applyUserFilter = () => {
    updateFilters({ user: userEmail.trim() || undefined });
  };

  const clearFilters = () => {
    setStatusFilter('');
    setTypeFilter('');
    setUserEmail('');
    setFromDate('');
    setToDate('');
    setFilters({});
  };

  const transactions = txData?.items || [];
  const isEmpty = !txLoading && !showError && transactions.length === 0;

  const statusBadgeClass = (s: string) => {
    switch (s) {
      case 'Succeeded': return 'bg-green-600/15 text-green-700 dark:text-green-500 border-green-600/25';
      case 'Pending':   return 'bg-amber-600/15 text-amber-700 dark:text-amber-500 border-amber-600/25';
      case 'Failed':    return 'bg-red-600/15 text-red-700 dark:text-red-500 border-red-600/25';
      default:          return 'bg-[var(--surface-overlay)] text-[var(--text-secondary)] border-[var(--border-subtle)]';
    }
  };

  const typeLabel = (type: string) => typeOptions.find((o) => o.value === type)?.label ?? type;

  const totalAmount = transactions
    .filter((tx) => tx.status === 'Succeeded')
    .reduce((acc, tx) => acc + Number(tx.amount || 0), 0);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden">
      <div className="mb-5 sm:mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[var(--text-primary)]">
            {t('adminTransactionList.title', 'Transactions')}
          </h1>
          <p className="mt-1.5 text-sm text-[var(--text-secondary)]">
            {t('adminTransactionList.subtitle', 'Every payment, refund, and credit adjustment across the platform.')}
          </p>
        </div>
        <div className="text-right sm:flex-shrink-0">
          <div className="text-3xl font-bold text-[var(--text-primary)] tabular-nums">{fmtMoney(totalAmount, 2)}</div>
          <div className="text-xs font-medium uppercase tracking-wider text-[var(--text-muted)]">
            {t('adminTransactionList.totalSucceeded', 'Total (succeeded)')}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-5 sm:mb-6 p-4 bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] overflow-visible">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 overflow-visible">
          <Select<string>
            label={t('adminTransactionList.filters.statusLabel', 'Status')}
            value={statusFilter}
            onChange={handleStatus}
            options={statusOptions}
          />
          <Select<string>
            label={t('adminTransactionList.filters.typeLabel', 'Type')}
            value={typeFilter}
            onChange={handleType}
            options={typeOptions}
          />
          <div>
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">
              {t('adminTransactionList.filters.userEmail', 'User email')}
            </label>
            <div className="flex gap-2">
              <Input
                type="text"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') applyUserFilter(); }}
                placeholder="user@example.com"
                className="flex-1"
              />
              <Button onClick={applyUserFilter} variant="outline" className="flex-shrink-0">
                {t('common.search', 'Search')}
              </Button>
            </div>
          </div>
          <DatePicker
            label={t('adminTransactionList.filters.fromDate', 'From date')}
            value={fromDate}
            onChange={handleFrom}
            placeholder="DD/MM/YYYY"
          />
          <DatePicker
            label={t('adminTransactionList.filters.toDate', 'To date')}
            value={toDate}
            onChange={handleTo}
            placeholder="DD/MM/YYYY"
          />
          <div className="flex items-end">
            <Button onClick={clearFilters} variant="outline" className="w-full">
              {t('adminTransactionList.filters.clear', 'Clear filters')}
            </Button>
          </div>
        </div>
      </div>

      {/* Error */}
      {showError && (
        <div className="mb-5 p-4 bg-red-600/10 border border-red-600/25 rounded-lg">
          <p className="text-sm text-red-700 dark:text-red-500">{errorMessage}</p>
        </div>
      )}

      {/* Loading */}
      {txLoading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-[var(--accent-primary)] border-t-transparent mx-auto"></div>
          <p className="mt-4 text-sm text-[var(--text-secondary)]">{t('common.loading', 'Loading…')}</p>
        </div>
      )}

      {/* Empty */}
      {isEmpty && (
        <div className="text-center py-12 bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)]">
          <p className="text-sm text-[var(--text-secondary)]">
            {t('adminTransactionList.empty', 'No transactions match the current filters.')}
          </p>
        </div>
      )}

      {/* Table */}
      {!txLoading && !isEmpty && !showError && (
        <div className="bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead className="bg-[var(--surface-raised)]">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">
                    {t('adminTransactionList.col.id', 'ID')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">
                    {t('adminTransactionList.col.date', 'Date')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">
                    {t('adminTransactionList.col.user', 'User')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">
                    {t('adminTransactionList.col.type', 'Type')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">
                    {t('adminTransactionList.col.amount', 'Amount')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">
                    {t('adminTransactionList.col.status', 'Status')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-subtle)]">
                {transactions.map((tx) => {
                  const user = userById.get(tx.userId);
                  return (
                    <tr
                      key={tx.transactionId}
                      className="transition-colors"
                    >
                      <td className="px-4 py-3 text-sm font-mono text-[var(--text-secondary)]">#{tx.transactionId}</td>
                      <td className="px-4 py-3 text-sm text-[var(--text-primary)] whitespace-nowrap">
                        {new Date(tx.date).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {user ? (
                          <div className="min-w-0">
                            <div className="font-medium text-[var(--text-primary)] truncate">
                              {user.name} {user.surname}
                            </div>
                            <div className="text-xs text-[var(--text-muted)] truncate">{user.email}</div>
                          </div>
                        ) : (
                          <span className="text-[var(--text-muted)]">#{tx.userId}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-[var(--text-secondary)] whitespace-nowrap">
                        {typeLabel(tx.type)}
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-[var(--text-primary)] text-right tabular-nums whitespace-nowrap">
                        {fmtMoney(tx.amount, 2)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full border ${statusBadgeClass(tx.status)}`}>
                          {tx.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminTransactionListPage;
